import { useCallback, useEffect, useState } from 'react';
import { replaceDashesWithUnderscores } from '@/utils/helpers';
import { config } from '@/api/common';
import { useAuthContext } from '@/contexts/AuthContext';

interface FetchProps {
    url: string;
    method: 'GET' | 'POST' | 'PUT' | 'DELETE';
    body?: BodyInit;
    immediate?: boolean;
}

export default function useFetch<T>(props: FetchProps) {
    const { url, method, body } = props;
    const onMount = props.immediate !== undefined ? props.immediate : true;
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<T | null>(null);
    const [response, setResponse] = useState<Response | null>(null);
    const [status, setStatus] = useState<number>(404);
    const { logout, getAuthedHeaders } = useAuthContext();

    const execute = useCallback(async (abortSignal?: AbortSignal) => {
        try {
            if (error) setError(null);
            setLoading(true);
            let response;
            
            try {
                response = await fetch(config.BASE_URL + url, {
                    method: method || 'GET',
                    body: body,
                    headers: getAuthedHeaders(),
                    signal: abortSignal
                });
            } catch (networkError) {
                console.error('Network error:', networkError);
                setData(null);
                setError('Connection failed - Server may be unreachable');
                setLoading(false);
                return;
            }

            setStatus(response.status);
            setResponse(response);

            // Handle 401 immediately before trying to parse JSON
            if (response.status === 401) {
                console.error('Authorization error');
                setData(null);
                setError('Unauthorized');
                logout();
                setLoading(false);
                return;
            }

            let json;
            try {
                json = await response.json();
            } catch (parseError) {
                const message = `Failed to parse response: ${response.statusText}`;
                setData(null);
                setError(message);
                setLoading(false);
                return;
            }

            if (!response.ok) {
                const message = json.message || 'An error occurred';
                setData(null);
                setError(message);
            }
            else {
                const data = json as T;
                setData(replaceDashesWithUnderscores(data));
                setError(null);
            }
            setLoading(false);
        }
        catch (error) {
            if (error instanceof DOMException && error.name === 'AbortError') {
                console.warn('Fetch aborted:', 'Unmounted component');
            } else {
                console.error('Unexpected error:', error);
                setError(error instanceof Error ? error.message : 'An error occurred');
                setLoading(false);
            }
        }
    }, [url, method, body, logout]);
    useEffect(() => {
        const abortController = new AbortController();
        onMount && execute(abortController.signal);
        return () => {
            abortController.abort();
        };
    }, [execute, onMount]);
    return { response, data, loading, error, status, execute };
}